<script lang="ts">
  import type { ReaderFrameItem } from '../../types/reader.interface';
  import InlineSvg from '../elements/InlineSvg.svelte';
  import { favoriteLibraryNames } from '../../store';
  import { createEventDispatcher, getContext } from 'svelte';
  import { Context } from '../../types/context.constants';
  import Tooltip from '../elements/Tooltip.svelte';
  import { trimEndingSlash } from '../../lib/url';

  export let item: ReaderFrameItem;

  const useFavorite = getContext(Context.USE_FAVORITE);
  const dispatch = createEventDispatcher();

  let shareLinks = [
    {
      text: 'Share on Twitter',
      iconUrl: '/img/icon/library/twitter.svg',
      link: `https://twitter.com/intent/tweet?hashtags=thestocksim%2Cdesign&size=large&original_referer=https%3A%2F%2Fthestocks.im%2F&text=Check%20this%20out&via=thestocksim&url=`,
    },
    {
      text: 'Share on WhatsApp',
      iconUrl: '/img/icon/library/whatsapp.svg',
      link: 'https://api.whatsapp.com/send?text=Check%20this%20out%20',
    },
    // {
    //   text: 'Share on Slack',
    //   iconUrl: '/img/icon/library/slack.svg',
    //   link: 'https://slack.com',
    // },
  ];
  let copyText: HTMLSpanElement;
  let rfAddress: HTMLParagraphElement;
  let selectedText: string = '';

  $: isFavorite = $favoriteLibraryNames.includes(item.name);

  function onFavoriteClicked() {
    if (isFavorite) {
      favoriteLibraryNames.remove(item.name);
    } else {
      dispatch('saved');
      favoriteLibraryNames.add(item.name);
    }
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(item.url).then(() => {
        copyText.innerText = 'Link Copied ✓';
        setTimeout(() => {
          copyText.innerText = 'Copy Link';
        }, 3000);
      });
    } catch (_) {
      copyText.innerText = 'Something went wrong!';
      setTimeout(() => {
        copyText.innerText = 'Copy Link';
      }, 3000);
    }
  }

  function selectAllText() {
    if (!selectedText && window.getSelection) {
      const range = document.createRange();
      range.selectNode(rfAddress);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
    }
    selectedText = window.getSelection().toString();
  }
</script>

<div class="rf-header">
  <div class="rf-bar">
    <span class="rf-icon">
      <InlineSvg src="/img/icon/library/lock.svg" width="12px" />
    </span>
    <p class="rf-address" bind:this={rfAddress} on:click={selectAllText}>
      {trimEndingSlash(item.featuredDisplayUrl || item.url)}
    </p>
    <ul class="rf-actions">
      {#if useFavorite}
        <li class:active={isFavorite}>
          <button class="heart" on:click={onFavoriteClicked}>
            <InlineSvg src="/img/icon/library/favorite.svg" width="12px" />
            {#if isFavorite}
              <span>Saved</span>
            {:else}
              <span>Save</span>
            {/if}
          </button>
        </li>
        <div class="divider" />
      {/if}
      <li>
        <Tooltip position="bottom">
          <a
            href={trimEndingSlash(
              item.featured ? item.url : `${item.url}?ref=thestocks.im`
            )}
            target="_blank"
          >
            <InlineSvg src="/img/icon/library/new-tab.svg" width="12px" />
          </a>
          <span class="tip" slot="tip">New Tab</span>
        </Tooltip>
      </li>
      <li class="share">
        <button>
          <InlineSvg src="/img/icon/library/share.svg" width="12px" />
        </button>
        <div class="dropdown-container">
          <ul class="dropdown">
            <li>
              <button on:click|preventDefault={copyUrl}>
                <InlineSvg src="/img/icon/library/copy.svg" width="16px" />
                <span bind:this={copyText}>Copy Link</span>
              </button>
            </li>
            {#each shareLinks as link (link.iconUrl)}
              <li>
                <a
                  href={link.link +
                    encodeURI(trimEndingSlash(item.featuredDisplayUrl || item.url))}
                  target="_blank"
                >
                  <InlineSvg src={link.iconUrl} width="16px" />
                  <span>{link.text}</span>
                </a>
              </li>
            {/each}
          </ul>
        </div>
      </li>
    </ul>
  </div>
</div>

<style lang="scss">
  @import '../../styles/mixins';

  .rf-header {
    background-color: var(--color-blue-gray--100);
    padding: 8px;
  }

  .rf-bar {
    height: 28px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    background-color: var(--color-bg--lightest);
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
    border-radius: 5px;
  }

  .rf-icon {
    display: inline-flex;
    width: 12px;
    height: 12px;
    --svg-fill: var(--color-blue-gray--200);
    margin-right: 8px;
  }

  .rf-address {
    @include font(Medium);
    @include overflow-ellipsis();
    flex: 1;
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.01em;
    color: var(--color-blue-gray--400);
    padding-bottom: 1px;
  }

  .rf-actions {
    display: flex;
    align-items: center;
    margin-left: auto;

    li {
      display: inline-flex;
      margin-left: 8px;
      padding: 0;
      height: 28px;
      align-items: center;
      justify-content: center;
      --svg-fill: var(--color-blue-gray--300);
      --svg-width: 12px;
      --svg-height: 12px;

      span {
        color: var(--color-blue-gray--300);
      }

      .tip {
        color: var(--color-blue-gray--50);
      }
      &:not(.active):hover {
        --svg-fill: var(--color-blue-gray--700);
        .heart {
          span {
            color: var(--color-blue-gray--700);
          }

          --svg-fill: transparent;
          --svg-stroke: var(--color-blue-gray--700);
        }
      }

      .heart {
        --svg-fill: transparent;
        --svg-stroke: var(--color-blue-gray--300);
      }

      &.active {
        button {
          --svg-fill: var(--color-red--500);
          --svg-stroke: var(--color-red--500);
          span {
            color: var(--color-red--500);
          }
        }
      }
    }

    button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: transparent;
      border: none;
      padding: 0;
      margin: 0;
      outline: none;
      cursor: pointer;

      span {
        font-size: 11px;
        margin-left: 6px;
        margin-bottom: 1px;
        color: var(--color-blue-gray--300);
      }
    }

    .divider {
      width: 16px;
      height: 16px;
      border-right: 1px solid var(--color-blue-gray--100);
      margin-right: 8px;
    }

    .share {
      position: relative;
      margin-left: 16px;

      .dropdown-container {
        position: absolute;
        top: 15px;
        right: -8px;
        padding: 0;
        padding-top: 10px;
        z-index: 1;
        pointer-events: none;
      }
      .dropdown {
        display: flex;
        transform: translateY(-12px);
        opacity: 0;
        flex-direction: column;
        align-content: center;
        justify-content: flex-start;
        align-items: flex-start;
        white-space: nowrap;
        padding: 8px 0;
        background-color: var(--color-bg--lightest);
        box-shadow: 0px 20px 56px rgba(47, 45, 94, 0.06),
          0px 6px 20px rgba(47, 45, 94, 0.06);
        border-radius: 10px;
        transition: all 0.1s ease-in;
        li {
          display: block;
          width: 100%;
          height: auto;
          margin: 0;
          --svg-width: 20px;
          --svg-height: 20px;

          a,
          button {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 10px 20px;
            height: 100%;
            width: 100%;
            --svg-fill: var(--color-blue-gray--300);

            span {
              @include font(Semibold);
              font-size: 13px;
              line-height: 20px;
              font-weight: 600;
              color: var(--color-blue-gray--400);
              margin-top: 2px;
              margin-left: 12px;
            }

            &:hover {
              background-color: var(--color-blue-gray--50);
              --svg-fill: var(--color-blue-gray--500);

              span {
                color: var(--color-blue-gray--500);
              }
            }
          }
          button:hover {
            :global(svg path) {
              stroke: var(--color-blue-gray--500);
            }

            :global(svg rect) {
              stroke: var(--color-blue-gray--500);
            }
          }
        }

        button {
          :global(svg path) {
            stroke: var(--color-blue-gray--300);
          }

          :global(svg rect) {
            stroke: var(--color-blue-gray--300);
          }
        }
      }

      &:hover {
        .dropdown-container {
          pointer-events: all;
        }
        .dropdown {
          opacity: 1;
          transform: translateY(6px);
        }
      }
    }
  }

  :global(.dark-mode) {
    .rf-header {
      background-color: var(--color-blue-gray--800);
    }

    .rf-bar {
      background-color: var(--color-blue-gray--600);
    }

    .rf-address {
      color: var(--color-blue-gray--100);
      &::selection {
        color: var(--color-blue-gray--800);
        background: var(--color-blue-gray--150);
      }
    }

    .rf-actions {
      li:not(.active) {
        --svg-fill: var(--color-blue-gray--150);
        .heart {
          --svg-stroke: var(--color-blue-gray--150);
          span {
            color: var(--color-blue-gray--150);
          }
        }
        &:hover {
          --svg-fill: var(--color-blue-gray--50);
          .heart {
            --svg-stroke: var(--color-blue-gray--50);
            span {
              color: var(--color-blue-gray--50);
            }
          }
        }
      }

      .divider {
        border-right: 1px solid var(--color-blue-gray--400);
      }

      .share {
        .dropdown {
          background-color: var(--color-blue-gray--700);
          box-shadow: var(--card-shadow-dark);

          li {
            a,
            button {
              --svg-fill: var(--color-blue-gray--200);

              span {
                color: var(--color-blue-gray--200);
              }

              &:hover {
                background-color: var(--color-blue-gray--600);
                --svg-fill: var(--color-blue-gray--50);

                span {
                  color: var(--color-blue-gray--50);
                }
              }
            }

            button:hover {
              :global(svg path) {
                stroke: var(--color-blue-gray--50);
              }

              :global(svg rect) {
                stroke: var(--color-blue-gray--50);
              }
            }
          }

          button {
            :global(svg path) {
              stroke: var(--color-blue-gray--200);
            }

            :global(svg rect) {
              stroke: var(--color-blue-gray--200);
            }
          }
        }
      }
    }
  }
</style>
